import { useCallback, useEffect, useRef, useState } from 'react'
import axios from '@fs/zion-axios'
import Cache from '@fs/zion-cache'
import { sgBaseUrl } from '@fs/zion-config'
import { i18n } from '@fs/zion-locale'
import { getUser } from '@fs/zion-user'

const cache = new Cache({ storeName: 'generations_count', type: 'session' })

export default function useGenerationCount(personId, maxGenerations = 7) {
  const [generationsCount, setGenerationsCount] = useState(null)
  const [totalPeople, setTotalPeople] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [fetchCount, setFetchCount] = useState(0)
  const bustCacheRef = useRef(false)

  const refetch = useCallback(() => {
    bustCacheRef.current = true
    setFetchCount((c) => c + 1)
  }, [])

  useEffect(() => {
    let isCancelled = false

    const fetchAndCount = async () => {
      try {
        const needsUserLookup = personId === 'undefined' || !personId
        const id = needsUserLookup ? (await getUser()).personId : personId
        const cacheKey = `${id}_${maxGenerations}`

        if (bustCacheRef.current) {
          await cache.removeItem(cacheKey)
          bustCacheRef.current = false
        }

        // Check cache first
        const cachedCount = await cache.getItem(cacheKey)
        if (cachedCount !== null && cachedCount !== undefined) {
          if (!isCancelled) {
            setGenerationsCount(cachedCount.generationCount)
            setTotalPeople(cachedCount.totalPeople)
            setError(null)
            setLoading(false)
          }
          return
        }

        // If not in cache, fetch from API
        setLoading(true)
        setError(null)

        const url = `${sgBaseUrl}/service/tree/tf/pedigree/hourglass/${id}?structure=graph&gensup=${maxGenerations}&gendown=0&includeAllSpousesDescendancy=false&includeSpouseAncestry=false&includeSuggestions=false&includePedigreeLooping=false`

        const response = await axios.get(url, {
          headers: { 'Accept-Language': i18n.language },
        })

        // Count total people in the tree (including the subject)
        let personCount = 1
        const countPeople = (parents) => {
          if (!parents) return
          const { parent1Pedigree, parent2Pedigree } = parents
          if (parent1Pedigree?.person?.id) {
            personCount++
            countPeople(parent1Pedigree.parents)
          }
          if (parent2Pedigree?.person?.id) {
            personCount++
            countPeople(parent2Pedigree.parents)
          }
        }
        countPeople(response.data.parents)

        let generationCount = 4
        if (personCount >= 26) generationCount = 6
        else if (personCount >= 17) generationCount = 5

        // Store in cache
        await cache.setItem(cacheKey, { generationCount, totalPeople: personCount })

        if (!isCancelled) {
          setGenerationsCount(generationCount)
          setTotalPeople(personCount)
        }
      } catch (err) {
        if (!isCancelled) {
          setError(err)
        }
      } finally {
        if (!isCancelled) {
          setLoading(false)
        }
      }
    }

    fetchAndCount()

    return () => {
      isCancelled = true
    }
  }, [personId, maxGenerations, fetchCount])

  return { generationsCount, totalPeople, loading, error, refetch }
}
